// Shared Swiper load promise — avoids 50 independent load listeners on the same script element
let _swiperReady = null;
function swiperReady() {
  if (_swiperReady) return _swiperReady;
  if (typeof Swiper !== 'undefined') {
    _swiperReady = Promise.resolve();
    return _swiperReady;
  }
  _swiperReady = new Promise((resolve) => {
    const script = document.querySelector('script[src*="swiper"]');
    if (script) {
      script.addEventListener('load', resolve, { once: true });
    }
  });
  return _swiperReady;
}

if (!customElements.get('product-card-listo')) {
  customElements.define(
    'product-card-listo',
    class ListoProductCard extends HTMLElement {
      connectedCallback() {
        try {
          this._abortController = new AbortController();
          const signal = this._abortController.signal;

          this._productCard = this.querySelector('div.product-card');
          if (!this._productCard) return;

          this._productMedia = this._productCard.querySelector(
            '.product-card-media'
          );
          this._productColorSlider = this._productCard.querySelector(
            '.product-card-slider'
          );
          this._productDetails = this._productCard.querySelector(
            '.product-card-details'
          );

          // Cheap ops — keep eager
          this._deconflictRadioNames();
          this._initQuickAdd(signal);

          // Defer expensive work (swatch listeners + Swiper) until visible
          this._observeVisibility(signal);
        } catch (e) {
          console.error('product-card-listo init failed:', e);
        }
      }

      disconnectedCallback() {
        this._abortController?.abort();
        this._intersectionObserver?.disconnect();
        this._intersectionObserver = null;
        if (this._swiper) {
          this._swiper.destroy(true, true);
          this._swiper = null;
        }
      }

      // --- Lazy initialization via IntersectionObserver ---

      _observeVisibility(signal) {
        const hasSlider = !!this._productColorSlider;
        const hasDeferred = !!this._productCard.querySelector('[data-src]');
        if (!hasSlider && !hasDeferred) return;

        this._intersectionObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                if (hasSlider) {
                  this._expandDeferredSwatches();
                  this._initSwatchListeners(signal);
                  this._initSwiper();
                }
                this._promoteDefImages();
                this._intersectionObserver.disconnect();
                this._intersectionObserver = null;
                break;
              }
            }
          },
          { rootMargin: '200px' }
        );
        this._intersectionObserver.observe(this._productCard);
      }

      // --- Deferred image promotion (data-src → src) ---

      _promoteDefImages() {
        const rIC = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
        rIC(
          () => {
            const deferred = this._productCard.querySelectorAll('[data-src]');
            for (const img of deferred) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
          },
          { timeout: 1500 }
        );
      }

      // --- Deferred swatch expansion ---
      // product-card-slider-listo.liquid renders the first slides plus the checked
      // colour, and emits the rest as JSON. Expanding here — before _initSwatchListeners
      // and _initSwiper — means the listener loop and Swiper both see the full set, so
      // nothing downstream needs to know these slides arrived late.
      _expandDeferredSwatches() {
        const payload = this._productColorSlider?.querySelector(
          'script[data-slider-deferred]'
        );
        if (!payload) return;

        let colors;
        try {
          colors = JSON.parse(payload.textContent);
        } catch (e) {
          // Leave the rendered slides alone rather than tearing the carousel apart.
          console.error('product-card-listo: bad deferred swatch payload', e);
          payload.remove();
          return;
        }

        const wrapper = this._productColorSlider.querySelector('.swiper-wrapper');
        if (!wrapper || !Array.isArray(colors)) return;

        const fragment = document.createDocumentFragment();
        for (const c of colors) {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';

          const label = document.createElement('label');
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = c.name;
          input.value = c.color;
          input.setAttribute('data-variant-image', c.image || '');
          input.setAttribute('data-variant-image-hover', c.hover || '');
          input.setAttribute('data-variant-price-actual', c.priceActual || '');
          input.setAttribute('data-variant-price-compare', c.priceCompare || '');
          input.setAttribute('data-variant-on-sale', String(!!c.onSale));
          input.setAttribute('data-variant-url', c.url || '');
          label.appendChild(input);

          if (c.thumb) {
            const img = document.createElement('img');
            img.src = c.thumb;
            img.alt = c.color;
            img.loading = 'lazy';
            label.appendChild(img);
          }

          const sr = document.createElement('span');
          sr.className = 'visually-hidden';
          sr.textContent = c.color;
          label.appendChild(sr);

          slide.appendChild(label);
          fragment.appendChild(slide);
        }
        wrapper.appendChild(fragment);
        payload.remove();
      }

      // --- Swatch change + hover preview (from product-card-listo.liquid) ---

      _initSwatchListeners(signal) {
        if (!this._productColorSlider) return;

        const variantImageFrame =
          this._productMedia?.querySelector('.image-variant');
        const colorInputs = this._productColorSlider.querySelectorAll('input');

        // Show variant image only after it loads (prevents broken image flash)
        if (variantImageFrame) {
          variantImageFrame.addEventListener(
            'load',
            () => variantImageFrame.classList.add('is-loaded'),
            { signal }
          );
        }

        this._productCard.addEventListener(
          'pointerenter',
          () => {
            const rIC = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
            rIC(
              () => {
                for (const input of colorInputs) {
                  for (const attr of [
                    'data-variant-image',
                    'data-variant-image-hover',
                  ]) {
                    const src = input.getAttribute(attr);
                    if (src) new Image().src = src;
                  }
                }
              },
              { timeout: 500 }
            );
          },
          { once: true, signal }
        );

        for (let i = 0; i < colorInputs.length; i++) {
          const colorInput = colorInputs[i];
          const colorInputLabel = colorInput.closest('label');
          if (!colorInputLabel) continue;

          const hoverImageSrc = colorInput.getAttribute('data-variant-image');

          colorInputLabel.addEventListener(
            'mouseenter',
            () => {
              if (variantImageFrame) {
                variantImageFrame.classList.remove('is-loaded');
                variantImageFrame.src = hoverImageSrc;
              }
            },
            { signal }
          );

          colorInputLabel.addEventListener(
            'mouseleave',
            () => {
              if (variantImageFrame) {
                variantImageFrame.classList.remove('is-loaded');
                variantImageFrame.src = '';
              }
            },
            { signal }
          );

          colorInput.addEventListener(
            'focus',
            () => {
              if (variantImageFrame) {
                variantImageFrame.classList.remove('is-loaded');
                variantImageFrame.src = hoverImageSrc;
              }
            },
            { signal }
          );

          colorInput.addEventListener(
            'blur',
            () => {
              if (variantImageFrame) {
                variantImageFrame.classList.remove('is-loaded');
                variantImageFrame.src = '';
              }
            },
            { signal }
          );

          colorInput.addEventListener(
            'change',
            () => this._changeVariant(colorInput),
            { signal }
          );
        }
      }

      _changeVariant(colorInput) {
        const newUrl = colorInput.getAttribute('data-variant-url');
        const newImage = colorInput.getAttribute('data-variant-image');
        const newImageHover = colorInput.getAttribute(
          'data-variant-image-hover'
        );
        const newPriceActual = colorInput.getAttribute(
          'data-variant-price-actual'
        );
        const newPriceCompare = colorInput.getAttribute(
          'data-variant-price-compare'
        );
        const newOnSale =
          colorInput.getAttribute('data-variant-on-sale') === 'true';

        const productMediaImageMain =
          this._productMedia?.querySelector('.image-main');
        const productMediaImageHover =
          this._productMedia?.querySelector('.image-hover');
        const productPriceActual =
          this._productDetails?.querySelector('.pricing .actual');
        const productPriceCompare =
          this._productDetails?.querySelector('.pricing .compare');
        const productSaleFlag =
          this._productDetails?.querySelector('.flag--sale');
        const productBundleFlag =
          this._productDetails?.querySelector('.flag--bundle');

        const primaryLink =
          this._productCard?.querySelector('.product-card-link');
        const detailsLink = this._productCard?.querySelector(
          '.product-card-details-link'
        );
        if (primaryLink) primaryLink.href = newUrl;
        if (detailsLink) detailsLink.href = newUrl;
        if (productMediaImageMain) {
          productMediaImageMain.src = newImage || '';
          productMediaImageMain.removeAttribute('srcset');
        }
        if (productMediaImageHover) {
          productMediaImageHover.removeAttribute('srcset');
          if (newImageHover) {
            productMediaImageHover.src = newImageHover;
            productMediaImageHover.hidden = false;
          } else {
            // This color has no image distinct from the one already shown. Hide the
            // layer rather than pointing it at an empty src, which resolves to the
            // page URL and fades a broken image in on hover.
            productMediaImageHover.removeAttribute('src');
            productMediaImageHover.hidden = true;
          }
        }
        if (productPriceActual) productPriceActual.textContent = newPriceActual;
        if (productPriceCompare)
          productPriceCompare.textContent = newPriceCompare;
        if (productSaleFlag) productSaleFlag.hidden = !newOnSale;
        // Keep SALE and Bundle + Save mutually exclusive: show the bundle flag
        // only when the selected variant is not on sale.
        if (productBundleFlag) productBundleFlag.hidden = newOnSale;
      }

      // --- Swiper carousel init (from product-card-slider-listo.liquid) ---

      _initSwiper() {
        if (!this._productColorSlider) return;
        swiperReady().then(() => this._createSwiper());
      }

      _createSwiper() {
        const swiperElement = this._productColorSlider.querySelector('.swiper');
        const prevButtonElement =
          this._productColorSlider.querySelector('.arrow-prev');
        const nextButtonElement =
          this._productColorSlider.querySelector('.arrow-next');

        if (!swiperElement) return;

        this._swiper = new Swiper(swiperElement, {
          slidesPerView: 4,
          slidesPerGroup: 3,
          spaceBetween: 4,
          watchSlidesProgress: true,
          mousewheel: {
            forceToAxis: true
          },
          navigation: {
            prevEl: prevButtonElement,
            nextEl: nextButtonElement
          },
          breakpoints: {
            900: {
              slidesPerView: 6.75,
              slidesPerGroup: 1,
              spaceBetween: 8
            }
          },
          nested: true,
          touchMoveStopPropagation: true,
          touchReleaseOnEdges: false,
          simulateTouch: true,
          on: {
            init: function () {
              swiperElement.style.visibility = 'visible';
              swiperElement.style.opacity = '1';
            },
            touchStart: function (swiper, event) {
              event.stopPropagation();
              event.stopImmediatePropagation();
            },
            touchMove: function (swiper, event) {
              event.stopPropagation();
              event.stopImmediatePropagation();
            },
            sliderMove: function (swiper, event) {
              event.stopPropagation();
              event.stopImmediatePropagation();
            }
          }
        });

        swiperElement.addEventListener(
          'wheel',
          function (event) {
            event.stopPropagation();
            event.stopImmediatePropagation();
          },
          { signal: this._abortController.signal }
        );
      }

      // --- Radio name deconfliction (from product-card-slider-listo.liquid) ---

      _deconflictRadioNames() {
        if (!this._productColorSlider || this._radioNamesDeconflicted) return;
        this._radioNamesDeconflicted = true;

        const colorInputs = this._productColorSlider.querySelectorAll('input');
        const sectionId = this._productCard.closest(
          '[id^="shopify-section-"]'
        )?.id;
        const blockId = this._productCard
          .closest('[data-block-id]')
          ?.getAttribute('data-block-id');

        for (let i = 0; i < colorInputs.length; i++) {
          const colorInput = colorInputs[i];

          if (blockId) {
            colorInput.name = blockId + '-' + colorInput.name;
          }

          if (sectionId) {
            colorInput.name = sectionId + '-' + colorInput.name;
          }
        }
      }

      // --- Quick-add (from product-card-quick-add-listo.liquid) ---

      _initQuickAdd(signal) {
        const quickAddButton = this.querySelector('.product-card-quick-add');
        if (!quickAddButton) return;

        const quickAddDialog = document.querySelector('dialog[data-quick-add]');
        if (!quickAddDialog) return;

        ListoProductCard._setupQuickAddDialog(quickAddDialog);

        quickAddButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            const cardLink =
              this._productCard.querySelector('.product-card-link');
            ListoProductCard._fetchQuickAdd(
              cardLink ? cardLink.href : '',
              quickAddDialog
            );
            ListoProductCard._openQuickAdd(quickAddDialog);
          },
          { signal }
        );
      }

      // showModal puts the dialog in the top layer, so no third-party script can
      // paint over it whatever z-index it claims. Throws if already open.
      static _openQuickAdd(dialog) {
        if (!dialog.open) dialog.showModal();
      }

      // One-time delegation on the section wrapper — survives all innerHTML swaps
      static _setupQuickAddDialog(dialog) {
        if (dialog._quickAddSetup) return;
        dialog._quickAddSetup = true;

        // A modal dialog's ::backdrop dispatches its clicks to the dialog itself,
        // so closing on a backdrop click is a dialog-scoped listener.
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });

        // The dialog is in the top layer but still a DOM child of the section, so
        // clicks inside it bubble here as usual.
        const section = dialog.closest('[id^="shopify-section"]');
        if (!section) return;

        // Capture phase: intercepts clicks before listeners added by inner scripts
        section.addEventListener(
          'click',
          (event) => {
            // Close button — ignore if inside a nested dialog (e.g. size guide)
            const closeBtn = event.target.closest('[data-close]');
            if (closeBtn && !closeBtn.closest('dialog[data-size-guide]')) {
              dialog.close();
              return;
            }

            // Bundle links — intercept before product-form-listo's listener navigates away
            const bundleLink = event.target.closest('a.bundle-link');
            if (bundleLink) {
              event.preventDefault();
              event.stopImmediatePropagation();
              const selections = new URLSearchParams();
              dialog
                .querySelectorAll('.value-grid input:checked, .gender-options input:checked')
                .forEach((input) => {
                  const option = input.getAttribute('data-option');
                  if (option) selections.append(option, input.value);
                });
              ListoProductCard._fetchQuickAdd(`${bundleLink.href.split(/[?#]/)[0]}?${selections}`, dialog);
            }
          },
          { capture: true }
        );
      }

      static _fetchQuickAdd(productUrl, dialog) {
        // Temporarily set params to allow dialog to parse them
        const currentUrl = window.location.toString();
        const cleanUrl = currentUrl.split('?')[0];
        const paramsToSet = productUrl.split('?')[1];
        const cleanUrlWithParams = paramsToSet ? `${cleanUrl}?${paramsToSet}` : cleanUrl;

        window.history.replaceState({}, '', cleanUrlWithParams);

        const sectionId = 'quick-add-listo';

        dialog.classList.add('is-loading');
        dialog.setAttribute('aria-busy', 'true');

        fetch(
          `${productUrl}${
            productUrl.includes('?') ? '&' : '?'
          }sections=${sectionId}`
        )
          .then((response) => response.json())
          .then((responseJson) => {
            const sectionHTML = responseJson[sectionId];
            const sectionNew = document.createElement('div');
            sectionNew.innerHTML = sectionHTML;
            HTMLUpdateUtility.setInnerHTML(
              dialog,
              sectionNew.querySelector('dialog[data-quick-add]').innerHTML
            );

            // Reset to clean url
            window.history.replaceState({}, '', currentUrl);

            // Override gallery Swiper for single-slide QuickShop layout
            const swiperEl = dialog.querySelector('.main-container .swiper');
            if (swiperEl && swiperEl.swiper) {
              swiperEl.swiper.params.slidesPerView = 1;
              swiperEl.swiper.params.spaceBetween = 0;
              swiperEl.swiper.params.breakpoints = {};
              swiperEl.swiper.update();
            }

            // showModal focused the close button, and the swap above just
            // destroyed it, dropping focus to the body. Put it back so a
            // keyboard or screen reader user keeps their place in the drawer.
            if (dialog.open && !dialog.contains(document.activeElement)) {
              const closeButton = dialog.querySelector('button[data-close]');
              if (closeButton) closeButton.focus();
            }

            dialog.classList.remove('is-loading');
            dialog.removeAttribute('aria-busy');
          })
          .catch((err) => {
            console.error('Quick add fetch failed:', err);
            dialog.classList.remove('is-loading');
            dialog.removeAttribute('aria-busy');
          });
      }
    }
  );
}
